'use strict';

const STEER_CONTENT_GROUP = 'tires';
const STEER_EXPERIENCE_STORAGE_KEY = 'rp-steer-experience';
const STEER_LANDING_PATH = '/';
const STEER_TRAFFIC_SOURCE = 'steer';

const STEER_GA4_EVENT = {
	ADD_TO_CART: 'add_to_cart',
	TIRE_EXPERIENCE_EXIT: 'tire_experience_exit',
	VEHICLE_PICKER_INITIALIZE: 'vehicle_picker_initialize',
	VIEW_ITEM: 'view_item',
};

/** @type {string} */
let lastViewedItemKey = '';

/**
 * @param {unknown} value
 * @returns {string}
 */
function readTrimmedString(value) {
	return typeof value === 'string' ? value.trim() : '';
}

/**
 * @param {unknown} value
 * @returns {number | null}
 */
function readOptionalPrice(value) {
	if (typeof value === 'number' && Number.isFinite(value)) {
		return value;
	}

	if (typeof value === 'string' && value !== '') {
		const parsed = Number.parseFloat(value);

		return Number.isFinite(parsed) ? parsed : null;
	}

	return null;
}

/**
 * @param {string} eventName
 * @param {Record<string, unknown>} payload
 * @returns {void}
 */
function trackSteerGa4Event(eventName, payload) {
	const track = globalThis.rp_app?.track;
	if (typeof track?.track_steer_ga4_event === 'function') {
		track.track_steer_ga4_event(eventName, payload);
		return;
	}

	if (typeof track?.track_ec_general !== 'function') {
		return;
	}

	track.track_ec_general(eventName, payload);
}

/**
 * @param {string} [cookieString]
 * @returns {string}
 */
function readGa4ClientIdFromCookie(cookieString) {
	let cookies = '';
	if (typeof cookieString === 'string') {
		cookies = cookieString;
	} else if (typeof globalThis.document?.cookie === 'string') {
		cookies = globalThis.document.cookie;
	}
	if (cookies === '') {
		return '';
	}

	const match = /(?:^|;\s*)_ga=GA\d+\.\d+\.([^;]+)/.exec(cookies);

	return match ? match[1] : '';
}

/**
 * @param {string} [visitorId]
 * @returns {string}
 */
function resolveSteerVisitorId(visitorId) {
	const fromArg = readTrimmedString(visitorId);
	if (fromArg !== '') {
		return fromArg;
	}

	return readGa4ClientIdFromCookie();
}

function visitorIdFields(visitorId) {
	const normalized = resolveSteerVisitorId(visitorId);

	return normalized === '' ? {} : { visitor_id: normalized };
}

/**
 * Same sources as cart.js GA4 add_to_cart (`window.CurrencyCode`, then the live cart).
 *
 * @returns {string}
 */
function readSteerCurrencyCode() {
	const fromWindow = readTrimmedString(globalThis.CurrencyCode);
	if (fromWindow !== '') {
		return fromWindow;
	}

	return readTrimmedString(globalThis.rp_app?.cart?._currency_code);
}

/**
 * STEER emit paths like `/brands/...`. GA4 Explorations expect `/tires/...`.
 *
 * @param {string} path
 * @returns {string}
 */
function toWebstoreSteerPath(path) {
	const trimmed = readTrimmedString(path);
	if (trimmed === '' || trimmed === '/') {
		return '/tires';
	}

	if (trimmed === '/tires' || trimmed.startsWith('/tires/') || trimmed.startsWith('/tires?')) {
		return trimmed;
	}

	return trimmed.startsWith('/') ? `/tires${trimmed}` : `/tires/${trimmed}`;
}

/**
 * @param {{ brand: string, mpn: string, name?: string, price?: number | null, visitorId?: string }} params
 * @returns {Record<string, unknown>}
 */
function buildSteerViewItemPayload({ brand, mpn, name, price, visitorId }) {
	const item = {
		item_brand: brand,
		item_id: mpn,
	};
	const itemName = readTrimmedString(name);
	if (itemName !== '') {
		item.item_name = itemName;
	}
	if (price !== null && price !== undefined) {
		item.price = price;
	}

	return {
		items: [item],
		...visitorIdFields(visitorId),
	};
}

/**
 * @param {{ brand: string, mpn: string, quantity: number, name?: string, price?: number | null, visitorId?: string }} params
 * @returns {Record<string, unknown>}
 */
function buildSteerAddToCartPayload({ brand, mpn, quantity, name, price, visitorId }) {
	const item = {
		item_brand: brand,
		item_id: mpn,
		quantity,
	};
	const itemName = readTrimmedString(name);
	if (itemName !== '') {
		item.item_name = itemName;
	}
	if (price !== null && price !== undefined) {
		item.price = price;
	}

	const payload = {
		items: [item],
		traffic_source: STEER_TRAFFIC_SOURCE,
		...visitorIdFields(visitorId),
	};
	if (price !== null && price !== undefined) {
		payload.value = price * quantity;
		const currency = readSteerCurrencyCode();
		if (currency !== '') {
			payload.currency = currency;
		}
	}

	return payload;
}

/**
 * @param {{ pagePath: string, visitorId?: string }} params
 * @returns {Record<string, unknown>}
 */
function buildSteerExitPayload({ pagePath, visitorId }) {
	return {
		content_group: STEER_CONTENT_GROUP,
		page_path: pagePath,
		...visitorIdFields(visitorId),
	};
}

/**
 * @param {string} brand
 * @param {string} mpn
 * @returns {boolean}
 */
function rememberViewedItem(brand, mpn) {
	if (brand === '' || mpn === '') {
		return false;
	}

	const key = `${brand}|${mpn}`;
	if (key === lastViewedItemKey) {
		return false;
	}

	lastViewedItemKey = key;

	return true;
}

/**
 * Webstore paths that keep the STEER island mounted.
 *
 * @param {string | null | undefined} pathname
 * @returns {boolean}
 */
function isSteerExperiencePath(pathname) {
	const path = readTrimmedString(pathname).split('?')[0];

	return path === '/tires' || path.startsWith('/tires/');
}

/** @type {boolean} */
let historyTrackingRegistered = false;

/** @type {((data: unknown, unused: string, url?: string | URL | null) => void) | null} */
let originalHistoryPushState = null;

/** @type {((data: unknown, unused: string, url?: string | URL | null) => void) | null} */
let originalHistoryReplaceState = null;

/**
 * @param {string | URL | null | undefined} url
 * @returns {string}
 */
function readPathnameFromHistoryUrl(url) {
	const fallback = readTrimmedString(globalThis.location?.pathname);
	if (typeof url !== 'string' && !(url instanceof URL)) {
		return fallback;
	}

	try {
		return new URL(url, globalThis.location?.origin || 'https://steer.invalid').pathname;
	} catch {
		return fallback;
	}
}

/**
 * Params for the generic page_view that already fires on /tires (history / gtag config).
 * Empty on every other path so search and parts stay unchanged.
 *
 * @param {string} [pathname]
 * @returns {Record<string, string>}
 */
function buildSteerGtagPageContext(pathname) {
	const path = readTrimmedString(pathname) || readTrimmedString(globalThis.location?.pathname);
	if (!isSteerExperiencePath(path)) {
		return {};
	}

	const visitorId = readGa4ClientIdFromCookie();
	const origin = readTrimmedString(globalThis.location?.origin);
	const context = {
		content_group: STEER_CONTENT_GROUP,
		page_path: path,
	};
	if (origin !== '') {
		context.page_location = `${origin}${path}`;
	}
	if (visitorId !== '') {
		context.visitor_id = visitorId;
	}

	return context;
}

/**
 * STEER soft-navigates with history; GA4 then sends the normal page_view.
 * Set the AC fields on that hit before pushState. Clear them off /tires.
 *
 * @param {string | URL | null | undefined} [url]
 * @returns {boolean}
 */
function applySteerGtagPageContext(url) {
	const gtag = globalThis.gtag;
	if (typeof gtag !== 'function') {
		return false;
	}

	const pathname = readPathnameFromHistoryUrl(url);
	if (!isSteerExperiencePath(pathname)) {
		gtag('set', {
			content_group: '',
			visitor_id: '',
		});

		return false;
	}

	gtag('set', buildSteerGtagPageContext(pathname));

	return true;
}

function onSteerHistoryPopState() {
	applySteerGtagPageContext(globalThis.location?.href);
}

/**
 * Loaded from main.js on every storefront page, so this wraps history site-wide.
 * /tires* gets the STEER fields before GA4's history page_view. Every other route
 * only clears those fields.
 *
 * @returns {void}
 */
function registerSteerHistoryTracking() {
	const historyObj = globalThis.history;
	if (historyTrackingRegistered || typeof historyObj?.pushState !== 'function') {
		return;
	}

	historyTrackingRegistered = true;
	originalHistoryPushState = historyObj.pushState;
	originalHistoryReplaceState = historyObj.replaceState;

	historyObj.pushState = function pushState(data, unused, url) {
		applySteerGtagPageContext(url);

		return originalHistoryPushState.call(historyObj, data, unused, url);
	};
	historyObj.replaceState = function replaceState(data, unused, url) {
		applySteerGtagPageContext(url);

		return originalHistoryReplaceState.call(historyObj, data, unused, url);
	};
	globalThis.addEventListener('popstate', onSteerHistoryPopState);
}

/**
 * @param {{ pagePath?: string, visitorId?: string }} [session]
 * @returns {void}
 */
function persistSteerExperienceSession(session) {
	const pagePath = toWebstoreSteerPath(readTrimmedString(session?.pagePath) || STEER_LANDING_PATH);
	const visitorId = readTrimmedString(session?.visitorId);

	try {
		globalThis.sessionStorage?.setItem(
			STEER_EXPERIENCE_STORAGE_KEY,
			JSON.stringify({ pagePath, visitorId }),
		);
	} catch {
		// Private mode / blocked storage must not break STEER.
	}
}

/**
 * @returns {{ pagePath: string, visitorId: string } | null}
 */
function readSteerExperienceSession() {
	try {
		const raw = globalThis.sessionStorage?.getItem(STEER_EXPERIENCE_STORAGE_KEY);
		if (typeof raw !== 'string' || raw === '') {
			return null;
		}

		const parsed = JSON.parse(raw);
		if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
			return null;
		}

		return {
			pagePath: readTrimmedString(parsed.pagePath) || '/tires',
			visitorId: readTrimmedString(parsed.visitorId),
		};
	} catch {
		return null;
	}
}

/**
 * @returns {void}
 */
function clearSteerExperienceSession() {
	try {
		globalThis.sessionStorage?.removeItem(STEER_EXPERIENCE_STORAGE_KEY);
	} catch {
		// ignore
	}
}

/**
 * Fires tire_experience_exit only after the shopper leaves /tires. Reload stays silent.
 *
 * @param {string | null | undefined} [pathname]
 * @returns {boolean}
 */
function flushSteerExitIfLeftExperience(pathname) {
	const currentPath = typeof pathname === 'string'
		? pathname
		: readTrimmedString(globalThis.location?.pathname);

	if (isSteerExperiencePath(currentPath)) {
		return false;
	}

	const session = readSteerExperienceSession();
	if (session === null) {
		return false;
	}

	clearSteerExperienceSession();
	trackSteerGa4Event(
		STEER_GA4_EVENT.TIRE_EXPERIENCE_EXIT,
		buildSteerExitPayload({
			pagePath: session.pagePath,
			visitorId: session.visitorId,
		}),
	);

	return true;
}

/**
 * bfcache restore keeps the mount node and module state. Clear once-guards so
 * view_item can fire again after resume.
 *
 * @returns {void}
 */
function clearSteerGa4SessionAfterResume() {
	lastViewedItemKey = '';
}

function resetSteerGa4StateForTests() {
	lastViewedItemKey = '';
	clearSteerExperienceSession();

	if (historyTrackingRegistered && originalHistoryPushState && originalHistoryReplaceState) {
		globalThis.history.pushState = originalHistoryPushState;
		globalThis.history.replaceState = originalHistoryReplaceState;
		globalThis.removeEventListener('popstate', onSteerHistoryPopState);
	}
	historyTrackingRegistered = false;
	originalHistoryPushState = null;
	originalHistoryReplaceState = null;
}

module.exports = {
	STEER_CONTENT_GROUP,
	STEER_EXPERIENCE_STORAGE_KEY,
	STEER_GA4_EVENT,
	STEER_LANDING_PATH,
	STEER_TRAFFIC_SOURCE,
	applySteerGtagPageContext,
	buildSteerAddToCartPayload,
	buildSteerExitPayload,
	buildSteerGtagPageContext,
	buildSteerViewItemPayload,
	clearSteerExperienceSession,
	clearSteerGa4SessionAfterResume,
	flushSteerExitIfLeftExperience,
	isSteerExperiencePath,
	persistSteerExperienceSession,
	readGa4ClientIdFromCookie,
	readOptionalPrice,
	readSteerExperienceSession,
	readTrimmedString,
	registerSteerHistoryTracking,
	rememberViewedItem,
	resetSteerGa4StateForTests,
	resolveSteerVisitorId,
	toWebstoreSteerPath,
	trackSteerGa4Event,
};
